import React, { useEffect, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { BackButton } from "../../components/common/MainButtonComponent";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import ViewEditor from "../../components/common/ViewEditor";
import { routes } from "../../helper/routes";
import { RichQuestionEditor } from "./QuestionEditorForm";
import { EditorState, convertFromRaw } from "draft-js";
import StudentPaperHeader from "./StudentPaperHeader";
import "./AssessShared.css";

/* ─────────────────────────────────────────────
   FALLBACK DATA (used only when no real data)
───────────────────────────────────────────── */
const fallbackParts = [
  {
    id: "part-1",
    title: "Part 1",
    sections: [
      {
        id: "section-1",
        title: "Section 1",
        questions: [
          { questionId: "ques-001" },
          { questionId: "ques-002" },
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────
   DRAFT-JS CONTENT PARSER
   Handles:
     • raw Draft.js object  { blocks: [...], entityMap: {} }
     • JSON string of the above
     • JSON string that is itself an array of Draft.js objects  ← your actual data
     • Plain strings
     • Arrays of any of the above
───────────────────────────────────────────── */
function formatQuestionContent(content) {
  if (content === null || content === undefined) return "";

  // Array – recurse over each element
  if (Array.isArray(content)) {
    return content
      .map((item) => formatQuestionContent(item))
      .filter(Boolean)
      .join("\n");
  }

  // String – try to parse as JSON first
  if (typeof content === "string") {
    const trimmed = content.trim();
    if (!trimmed) return "";

    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        return formatQuestionContent(parsed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  // Draft.js object with blocks array
  if (typeof content === "object") {
    if (Array.isArray(content.blocks)) {
      return content.blocks
        .map((block) => block?.text ?? "")
        .filter(Boolean)
        .join("\n");
    }

    if (typeof content.text === "string") {
      return content.text;
    }

    // Generic object – recurse over values
    return Object.values(content)
      .map((v) => formatQuestionContent(v))
      .filter(Boolean)
      .join("\n");
  }

  return String(content);
}

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const LETTER_OPTIONS = ["A", "B", "C", "D"];

function toArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
}

function parseDraftRaw(raw) {
  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

function getDraftTextContent(raw) {
  try {
    const parsed = parseDraftRaw(raw);
    const first = Array.isArray(parsed) ? parsed[0] : parsed;

    return first?.blocks?.map((block) => block.text).join("\n") || "";
  } catch {
    return "";
  }
}

function toEditorState(raw) {
  try {
    const parsed = parseDraftRaw(raw);
    const content = Array.isArray(parsed) ? parsed[0] : parsed;

    if (!content || typeof content !== "object" || !Array.isArray(content.blocks)) {
      return null;
    }

    return EditorState.createWithContent(convertFromRaw(content));
  } catch {
    return null;
  }
}

function getQuestionType(question) {
  return (
    question?.type ??
    question?.question_type_name ??
    question?.question_details?.question_type_name ??
    question?.question_details?.type ??
    question?.answer_type_name ??
    question?.answerTypeName ??
    "N/A"
  );
}

function getQuestionContentSource(question) {
  return (
    question?.content ??
    question?.question_content ??
    question?.question_text ??
    question?.question_details?.content ??
    question?.question_details?.question_content ??
    question?.raw?.content ??
    ""
  );
}

function normalizePreviewOptions(question) {
  const parsedContent = parseDraftRaw(question?.content);
  if (Array.isArray(parsedContent) && parsedContent.length > 1) {
    const draftOptions = parsedContent
      .slice(1, 5)
      .map((optionRaw) => ({
        raw: optionRaw,
        text: getDraftTextContent(optionRaw),
      }))
      .filter((option) => option.text);
    if (draftOptions.length) {
      return draftOptions;
    }
  }

  const source =
    question?.previewOptions ??
    question?.options ??
    question?.question_options ??
    question?.question_details?.options ??
    question?.raw?.options ??
    question?.raw?.question_options;

  const items = toArray(source);

  if (!items.length) {
    return ["Option A", "Option B", "Option C", "Option D"].map((text) => ({
      raw: text,
      text,
    }));
  }

  return items
    .slice(0, 4)
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        const text = item.label ?? item.text ?? item.option ?? item.value ?? item.title ?? "";
        return {
          raw: item.raw ?? item,
          text,
        };
      }
      const text = String(item);
      return {
        raw: item,
        text,
      };
    })
    .filter((option) => option.text);
}

function normalizeQuestion(question) {
  if (!question) return null;

  const raw = question.raw ?? question.question_details ?? question;
  const questionId = question.questionId ?? question.question_id ?? question.id;

  return {
    ...question,
    id: String(question.id ?? question.questionId ?? question.question_id ?? questionId ?? ""),
    questionId: Number(questionId ?? 0),
    name: question.name ?? question.title ?? raw?.title ?? `Question ${questionId ?? ""}`,
    title: question.title ?? raw?.title ?? "",
    content: getQuestionContentSource(question),
    type: getQuestionType(question),
    is_optional: Boolean(question.is_optional ?? question.answerRequired === false),
    previewOptions: normalizePreviewOptions(question),
    subQuestions: toArray(question.sub_questions_details).map(normalizeQuestion).filter(Boolean),
    is_passage: Boolean(question.is_passage ?? raw?.is_passage),
    passage_id: question.passage_id ?? raw?.passage_id ?? null,
    passage_content: question.passage_content ?? raw?.passage_content ?? "",
    raw,
  };
}

function groupQuestionsByPassage(questions = []) {
  const groups = [];

  questions.forEach((question) => {
    if (!question.passage_id) {
      groups.push({
        passage_id: null,
        passage_content: "",
        questions: [question],
      });

      return;
    }

    const existingGroup = groups.find(
      (group) =>
        String(group.passage_id) === String(question.passage_id)
    );

    if (existingGroup) {
      existingGroup.questions.push(question);
      return;
    }

    groups.push({
      passage_id: question.passage_id,
      passage_content: question.passage_content,
      questions: [question],
    });
  });

  return groups;
}

function normalizeSection(section) {
  if (!section) return null;

  return {
    ...section,
    id: String(section.id ?? section.section_id ?? section.part_section_id ?? ""),
    title: section.title ?? section.section_title ?? "Section",
    instructions: section.instructions ?? section.section_instruction ?? "",
    questionsCount: section.questionsCount ?? section.no_of_questions ?? section.questions?.length ?? 0,
    questions: toArray(section.questions).map(normalizeQuestion).filter(Boolean),
  };
}

function normalizePart(part) {
  if (!part) return null;

  return {
    ...part,
    id: String(part.id ?? part.part_id ?? ""),
    title: part.title ?? part.part_title ?? part.name ?? "Part",
    instructions: part.instructions ?? part.part_instruction ?? "",
    noOfSections: part.noOfSections ?? part.no_of_sections ?? part.sections?.length ?? 0,
    sections: toArray(part.sections).map(normalizeSection).filter(Boolean),
  };
}

/* ─────────────────────────────────────────────
   INSTRUCTIONS BOX
───────────────────────────────────────────── */
function InstructionsBox({ instructions, type = "section" }) {
  if (!instructions || !instructions.trim()) return null;

  return (
    <div className={`student-preview-instructions student-preview-instructions--${type}`}>
      <div className="student-preview-instructions__header">
        <span className="student-preview-instructions__title">
          {type === "part" ? "Part Instructions" : "Section Instructions"}
        </span>
      </div>
      <div className="student-preview-instructions__content">
        {instructions}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   QUESTION BODY
───────────────────────────────────────────── */
/* ─────────────────────────────────────────────
   QUESTION HEADER CONTENT
───────────────────────────────────────────── */
function QuestionHeaderContent({ question, showPassage = true }) {
  const questionContentSource = getQuestionContentSource(question);
  const isGroupedParent = Array.isArray(question.subQuestions) && question.subQuestions.length > 0;
  const isDescriptive = String(getQuestionType(question)).toUpperCase() !== "MCQ";
  const questionEditorState = toEditorState(questionContentSource);
  const passageEditorState = toEditorState(question.passage_content);

  if (isDescriptive) {
    return (
      <div className="student-preview-answer-box">
        {showPassage && passageEditorState && (
          <div className="student-preview-question-content">
            <ViewEditor editorState={passageEditorState} />
          </div>
        )}

        {questionEditorState ? (
          <div className="student-preview-question-content">
            <ViewEditor editorState={questionEditorState} />
          </div>
        ) : (
          <div className="student-preview-question-content">
            {formatQuestionContent(questionContentSource)}
          </div>
        )}
      </div>
    );
  }

  /* MCQ */
  const mcqContentParts = Array.isArray(parseDraftRaw(questionContentSource))
    ? parseDraftRaw(questionContentSource)
    : [];
  const promptValue = mcqContentParts.length > 0 ? mcqContentParts[0] : questionContentSource;
  const promptEditorState = toEditorState(promptValue);

  return (
    <>
      {showPassage && passageEditorState ? (
        <div className="student-preview-question-content">
          <ViewEditor editorState={passageEditorState} />
        </div>
      ) : null}
      {promptEditorState ? (
        <div className="student-preview-question-content">
          <ViewEditor editorState={promptEditorState} />
        </div>
      ) : (
        formatQuestionContent(promptValue) && (
          <div className="student-preview-question-content">
            {formatQuestionContent(promptValue)}
          </div>
        )
      )}
    </>
  );
}

/* ─────────────────────────────────────────────
   QUESTION BODY
───────────────────────────────────────────── */
function PreviewQuestionBody({ question, marks, isOptional }) {
  const isGroupedParent = Array.isArray(question.subQuestions) && question.subQuestions.length > 0;
  const isDescriptive = String(getQuestionType(question)).toUpperCase() !== "MCQ";

  if (isDescriptive) {
    return (
      <div className="student-preview-answer-box">
        {!isGroupedParent && (
          <>
            {/* Answers are intentionally hidden in student preview */}
            <div className="student-preview-answer-box__editor">
              <RichQuestionEditor readOnly />
            </div>
            <div className="student-preview-answer-box__placeholder">
              {isOptional ? "Add your answer here (optional)…" : "Add your answer here…"}
            </div>
          </>
        )}
      </div>
    );
  }

  /* MCQ */
  const options = normalizePreviewOptions(question);

  return (
    <>
      {/* Answers are intentionally hidden in student preview */}
      <div className="row g-3">
        {options.map((option, idx) => (
          <div
            className="col-12 col-md-6"
            key={`${question.id ?? question.questionId}-opt-${idx}`}
          >
            <label className="student-preview-choice">
              <span className="student-preview-choice__letter" aria-hidden="true">
                {LETTER_OPTIONS[idx] ?? idx + 1}
              </span>
              {toEditorState(option.raw) ? (
                <div className="student-preview-choice__content">
                  <ViewEditor editorState={toEditorState(option.raw)} />
                </div>
              ) : (
                <p>{option.text}</p>
              )}
            </label>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   QUESTION CARD
───────────────────────────────────────────── */
function QuestionCard({ question, displayNumber, questionMeta, isOptional, subQuestions = [], showPassage = true }) {
  const [isReviewed, setIsReviewed] = React.useState(false);
  const marks = questionMeta?.raw?.mark_allocation ?? questionMeta?.marks ?? question._rowMarks ?? null;
  const qType = question.type ?? "—";
  const displayType = qType === "Sub Question" && subQuestions.length > 0 ? "Main Question" : qType;
  const qTypeClass = String(qType)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const employeeType = sessionStorage.getItem("employeeType");
  const userType = sessionStorage.getItem("userType");
  const isDeopOrMasad = employeeType === "DEOP" || employeeType === "MASAD" || userType === "DEOP" || userType === "MASAD";

  return (
    <article className="student-preview-question-card" key={question.id ?? question.questionId}>
      <div className="student-preview-question-card__header">
        <div className="student-preview-question-card__number">
          Q{displayNumber}
        </div>
        <div className="student-preview-question-card__meta">
          {/* <h4 className="student-preview-question-card__name">{question.name}</h4> */}
          {/* <div className="student-preview-question-card__badges">
            <span className={`spq-badge spq-badge--type spq-badge--${qTypeClass || "unknown"}`}>
              {displayType}
            </span>
            {marks !== null && (
              <span className="spq-badge spq-badge--marks">
                {marks} {marks === 1 ? "mark" : "marks"}
              </span>
            )}
            {isOptional && (
              <span className="spq-badge spq-badge--optional">Optional</span>
            )}
          </div> */}
          <QuestionHeaderContent
            question={question}
            showPassage={showPassage}
          />
        </div>
        {!isDeopOrMasad && (
          <label className="student-preview-question-card__review">
            <input
              type="checkbox"
              checked={isReviewed}
              onChange={() => setIsReviewed(!isReviewed)}
            />
            <span />
            Mark for Review
          </label>
        )}
      </div>
      <div className="student-preview-question-card__body">
        <PreviewQuestionBody question={question} marks={marks} isOptional={isOptional} />
      </div>
      {subQuestions.length > 0 && (
        <div className="student-preview-subquestion-list">
          {subQuestions.map((subQuestion, subIndex) => {
            const subDisplayNumber = `${displayNumber}(${String.fromCharCode(97 + subIndex)})`;
            const subQuestionMeta = {
              raw: subQuestion.raw ?? subQuestion,
              marks: subQuestion.sub_mark_allocation ?? subQuestion.mark_allocation ?? null,
            };
            const subIsOptional = subQuestion.is_optional ?? false;

            return (
              <QuestionCard
                key={subQuestion.id ?? subQuestion.questionId ?? `${displayNumber}-sub-${subIndex}`}
                question={subQuestion}
                displayNumber={subDisplayNumber}
                questionMeta={subQuestionMeta}
                isOptional={subIsOptional}
              />
            );
          })}
        </div>
      )}
    </article>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
export default function AssessmentStudentPreviewPage({
  setOpenStudentView,
  assessment,
  parts = [],
  setupQuestionBank = [],
  metadataOptions = {},
}) {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // useEffect(() => {
  //   if (process.env.NODE_ENV === "production") return;

  //   console.log("AssessmentStudentPreviewPage incoming data", {
  //     assessment,
  //     parts,
  //     setupQuestionBank,
  //     metadataOptions,
  //     locationState: location.state,
  //   });
  // }, [assessment, parts, setupQuestionBank, location.state]);

  /* ── question lookup map: keyed by both id and questionId ── */
  const questionMap = useMemo(
    () =>
      Object.fromEntries(
        setupQuestionBank.flatMap((q) => {
          const normalized = normalizeQuestion(q);
          if (!normalized) return [];

          return [
            [String(normalized.id), normalized],
            [String(normalized.questionId), normalized],
          ];
        })
      ),
    [setupQuestionBank]
  );

  /* ── raw question meta (for marks etc.) keyed by questionId ── */
  const metaMap = useMemo(() => {
    const map = {};
    toArray(parts).forEach((part) => {
      const sections = toArray(part.sections);
      sections.forEach((section) => {
        const questions = toArray(section.questions);
        questions.forEach((row) => {
          const normalizedRow = normalizeQuestion(row);
          if (!normalizedRow) return;

          map[String(normalizedRow.questionId)] = {
            ...row,
            ...normalizedRow,
          };

          normalizedRow.subQuestions.forEach((subQuestion) => {
            map[String(subQuestion.questionId)] = {
              ...subQuestion,
              raw: subQuestion.raw ?? subQuestion,
            };
          });
        });
      });
    });
    return map;
  }, [parts]);

  /* ── resolve preview source ── */
  const previewSourceParts = location.state?.parts?.length
    ? location.state.parts
    : parts.length
      ? parts
      : fallbackParts;

  /* ── build enriched parts ── */
  const previewParts = useMemo(
    () =>
      toArray(previewSourceParts).map((part) => {
        const normalizedPart = normalizePart(part) ?? { sections: [] };

        return {
          ...normalizedPart,
          sections: normalizedPart.sections.map((section) => ({
            ...section,
            questions: toArray(section.questions).flatMap((row, rowIndex) => {
              const normalizedRow = normalizeQuestion(row);
              const found =
                questionMap[String(normalizedRow?.questionId ?? row.questionId ?? row.id)] ??
                normalizedRow;

              if (!found) return [];

              return [
                {
                  ...found,
                  id: String(found.id ?? found.questionId ?? row.id ?? row.questionId ?? ""),
                  questionId: Number(found.questionId ?? row.questionId ?? 0),
                  _rowMarks: row.marks ?? found._rowMarks ?? found.mark_allocation ?? null,
                  parentOrder: rowIndex,
                  subIndex: null,
                  answer: null,
                  subQuestions: found.subQuestions ?? [],
                },
              ];
            }),
          })),
        };
      }),
    [previewSourceParts, questionMap]
  );

  /* ── shared paper header data ── */
  const paperHeaderAssessment = useMemo(() => {
    const resolveOptionLabel = (options, value) => {
      if (value === null || value === undefined || value === "") return "";

      const match = toArray(options).find(
        (option) => String(option?.value) === String(value)
      );
      return match?.label || String(value);
    };

    const gradeValue = assessment?.grade_id ?? assessment?.grade;
    const subjectValue = assessment?.subject_id ?? assessment?.subject;
    const languageValue =
      assessment?.language_id ?? assessment?.language ?? assessment?.medium;

    return {
      ...assessment,
      assessment_details: {
        ...assessment,
        title: assessment?.title ?? assessment?.name ?? "Assessment",
        grade: resolveOptionLabel(metadataOptions?.gradeOptions, gradeValue),
        subject: resolveOptionLabel(metadataOptions?.subjectOptions, subjectValue),
        medium: resolveOptionLabel(metadataOptions?.languageOptions, languageValue),
        author: assessment?.owner ?? assessment?.createdBy ?? assessment?.author ?? "",
      },
    };
  }, [assessment, metadataOptions]);

  const handleBack = () => {
    if (typeof setOpenStudentView === "function") {
      setOpenStudentView(false);
      return;
    }
    history.push(`${routes.asses_lib}/setup/${id}`);
  };

  return (
    <ContentWrapper>
      <section className="assess-section student-preview-page">

        {/* ── Back action ── */}
        <div className="student-preview-actions">
          <BackButton label="" title="Back" onClick={handleBack} />
        </div>

        {/* ── Paper header ── */}
        <StudentPaperHeader
          assessment={paperHeaderAssessment}
          showStartTime={false}
        />
        {/* ── Parts ── */}
        {previewParts.map((part) => (
          <React.Fragment key={part.id}>
            <div className="student-preview-part-shell-1">
              <div className="student-preview-part-header">
                <h2>{part.title}</h2>
                {part.noOfSections && (
                  <span className="student-preview-part-meta">
                    {part.noOfSections} {part.noOfSections === 1 ? "Section" : "Sections"}
                  </span>
                )}
              </div>

              {/* Part Instructions */}
              {part.instructions && (
                <InstructionsBox
                  instructions={part.instructions}
                  type="part"
                />
              )}
            </div>

            {part.sections.map((section) => (
              <React.Fragment key={section.id}>
                <div className="student-preview-section-shell-1">
                  <div className="student-preview-section-header">
                    <h3>{section.title}</h3>
                    {section.questionsCount && (
                      <span className="student-preview-section-meta">
                        {section.questionsCount} {section.questionsCount === 1 ? "Question" : "Questions"}
                      </span>
                    )}
                  </div>

                  {/* Section Instructions */}
                  {section.instructions && (
                    <InstructionsBox
                      instructions={section.instructions}
                      type="section"
                    />
                  )}
                </div>

                {section.questions.length === 0 ? (
                  <p className="student-preview-empty">No questions in this section.</p>
                ) : (
                  <div className="student-preview-question-list">
                    {groupQuestionsByPassage(section.questions).map(
                      (passageGroup, groupIndex) => (
                        <React.Fragment
                          key={
                            passageGroup.passage_id
                              ? `passage-${passageGroup.passage_id}`
                              : `question-${groupIndex}`
                          }
                        >
                          {passageGroup.passage_id &&
                            toEditorState(passageGroup.passage_content) && (
                              <div className="student-preview-question-card">
                                <div className="student-preview-question-card__header">
                                  <div className="student-preview-question-card__meta">
                                    <div className="student-preview-question-content">
                                      <ViewEditor
                                        editorState={toEditorState(
                                          passageGroup.passage_content
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                          {passageGroup.questions.map((question) => {
                            const idx = section.questions.indexOf(question);

                            const parent =
                              typeof question.parentOrder === "number"
                                ? question.parentOrder
                                : null;

                            const subIndex =
                              question.subIndex !== undefined &&
                                question.subIndex !== null
                                ? question.subIndex
                                : null;

                            const displayNumber =
                              parent !== null
                                ? subIndex !== null
                                  ? `${parent + 1}(${String.fromCharCode(
                                    97 + subIndex
                                  )})`
                                  : `${parent + 1}`
                                : `${idx + 1}`;

                            const questionMeta =
                              metaMap[String(question.questionId)] ?? null;

                            const isOptional =
                              questionMeta?.allowOptionalQuestions ??
                              question.is_optional ??
                              false;

                            return (
                              <QuestionCard
                                key={question.id ?? question.questionId}
                                question={question}
                                displayNumber={displayNumber}
                                questionMeta={questionMeta}
                                isOptional={isOptional}
                                subQuestions={question.subQuestions ?? []}
                                showPassage={!passageGroup.passage_id}
                              />
                            );
                          })}
                        </React.Fragment>
                      )
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </section>
    </ContentWrapper>
  );
}
